import React, { FC, ReactNode } from 'react';
import { Checkbox, FormControl, FormHelperText, Grid, Typography } from '@mui/material';
import { FastField, FieldProps } from 'formik';

type CheckboxListItem = {
  id: number;
  // Allow flexible display fields (e.g., name, tag, title)
  [key: string]: string | number | null | undefined;
};

interface FormFieldCheckboxListProps {
  label?: string | ReactNode;
  name: string;
  listItems: Array<CheckboxListItem>;
  readonly?: boolean;
  autoFocus?: boolean;
  nameField?: string;
}

const FormFieldCheckboxList: FC<FormFieldCheckboxListProps> = ({
  label,
  name,
  listItems,
  nameField,
}: FormFieldCheckboxListProps) => {
  const itemMap = Array.isArray(listItems) ? listItems : [];

  return (
    <FastField name={name}>
      {({ field, meta, form }: FieldProps<number[]>) => {
        const currentValue = Array.isArray(field.value) ? field.value : [];
        const toggleValue = (id: number, checked: boolean) => {
          const next = checked ? Array.from(new Set([...currentValue, id])) : currentValue.filter((val) => val !== id);
          form.setFieldValue(name, next);
          form.setFieldTouched(name, true, false);
        };

        return (
          <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
            <FormControl error={!!meta.error && meta.touched} sx={{ width: '100%' }}>
              {label && (
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {label}
                </Typography>
              )}
              {itemMap.map((item) => (
                <Grid container key={`${name}_${item.id}`}>
                  <Grid item xs={3}>
                    <Checkbox
                      checked={currentValue.includes(item.id)}
                      onChange={(e) => toggleValue(item.id, e.target.checked)}
                    />
                  </Grid>
                  <Grid item xs={9} pt={1} pl={1}>
                    <Typography>{String(item[nameField ?? 'name'] ?? '')}</Typography>
                  </Grid>
                </Grid>
              ))}
              {(!itemMap || itemMap.length === 0) && (
                <Typography variant="body2" color="text.secondary">
                  —
                </Typography>
              )}
              {meta.error && meta.touched && <FormHelperText>{meta.error}</FormHelperText>}
            </FormControl>
          </fieldset>
        );
      }}
    </FastField>
  );
};

export default FormFieldCheckboxList;
